import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';

import LoginPage from '../pages/LoginPage';
import CommandCenterPage from '../pages/CommandCenterPage';
import JobsPage from '../pages/JobsPage';
import JobDetailPage from '../pages/JobDetailPage';
import CandidateDetailPage from '../pages/CandidateDetailPage';
import AssessmentListPage from '../pages/AssessmentListPage';
import AssessmentResultsPage from '../pages/AssessmentResultsPage';
import TakeAssessmentPage from '../pages/TakeAssessmentPage';
import InterviewListPage from '../pages/InterviewListPage';
import InterviewRoomPage from '../pages/InterviewRoomPage';
import InterviewDataRequestPage from '../pages/InterviewDataRequestPage';
import InterviewResultsPage from '../pages/InterviewResultsPage';
import CandidatePortalPage from '../pages/CandidatePortalPage';
import ApplicationDetailPage from '../pages/ApplicationDetailPage';
import AdminDashboardPage from '../pages/AdminDashboardPage';
import VerificationDashboardPage from '../pages/VerificationDashboardPage';
import VerificationDetailPage from '../pages/VerificationDetailPage';
import CandidateVerificationPage from '../pages/CandidateVerificationPage';
import PricingPage from '../pages/PricingPage';
import CaseStudyPage from '../pages/CaseStudyPage';
import MSAPage from '../pages/legal/MSAPage';
import PrivacyPolicyPage from '../pages/legal/PrivacyPolicyPage';
import BiometricPolicyPage from '../pages/legal/BiometricPolicyPage';
import SecurityPage from '../pages/legal/SecurityPage';
import DataRequestPage from '../pages/legal/DataRequestPage';
import PaymentPage from '../pages/PaymentPage';
import PaymentReturnPage from '../pages/PaymentReturnPage';
import CredentialPage from '../pages/CredentialPage';
import ApplicationsPage from '../pages/ApplicationsPage';
import JDBuilderPage from '../pages/JDBuilderPage';
import PipelinePage from '../pages/PipelinePage';
import AnalyticsPage from '../pages/AnalyticsPage';
import CreditUsagePage from '../pages/CreditUsagePage';
import SettingsPage from '../pages/SettingsPage';
import ConsentLogPage from '../pages/ConsentLogPage';
import DataRequestsAdminPage from '../pages/DataRequestsAdminPage';
import CandidateRequestsAdminPage from '../pages/CandidateRequestsAdminPage';
import AITransparencyPage from '../pages/AITransparencyPage';
import BillingPage from '../pages/BillingPage';
import MagicLinkPage from '../pages/MagicLinkPage';
import LandingPage from '../pages/LandingPage';
import PublicCareersPage from '../pages/public/PublicCareersPage';
import PublicJobPage from '../pages/public/PublicJobPage';

// Product pages
import AssessmentsProductPage from '../pages/products/AssessmentsPage';
import ScreeningProductPage from '../pages/products/ScreeningPage';
import BlockchainProductPage from '../pages/products/BlockchainPage';
import InterviewsProductPage from '../pages/products/InterviewsPage';
import EngageProductPage from '../pages/products/EngagePage';
import ComplianceProductPage from '../pages/products/CompliancePage';
import PipelineProductPage from '../pages/products/PipelinePage';
import AssessmentsGlobalPage from '../pages/AssessmentsGlobalPage';
import AIInterviewsHubPage from '../pages/AIInterviewsHubPage';
import AIInterviewPlanPage from '../pages/AIInterviewPlanPage';
import AIInterviewReportPage from '../pages/AIInterviewReportPage';
import AIInterviewCandidatePage from '../pages/AIInterviewCandidatePage';
import SchedulingPage from '../pages/SchedulingPage';
import CandidateSchedulePage from '../pages/CandidateSchedulePage';
import HumanInterviewRoomPage from '../pages/HumanInterviewRoomPage';
import CandidateJoinPage from '../pages/CandidateJoinPage';
import SharedAIReportPage from '../pages/SharedAIReportPage';
import ResumeIntelligenceListPage from '../pages/ResumeIntelligencePage';
import SourcingPage from '../pages/SourcingPage';
import CareersManagementPage from '../pages/CareersManagementPage';
import CredentialsManagePage from '../pages/CredentialsManagePage';
import CompliancePage from '../pages/CompliancePage';
import BehavioralAssessmentsPage from '../pages/BehavioralAssessmentsPage';
import BehavioralReportPage from '../pages/BehavioralReportPage';
import BehavioralTakePage from '../pages/BehavioralTakePage';

// Feature pages
import CertifiedAssessmentsPage from '../pages/features/CertifiedAssessmentsPage';
import AntiCheatingPage from '../pages/features/AntiCheatingPage';
import NFTCredentialsPage from '../pages/features/NFTCredentialsPage';
import ResumeIntelligencePage from '../pages/features/ResumeIntelligencePage';
import AIQuestionsPage from '../pages/features/AIQuestionsPage';
import IntegrationsFeaturePage from '../pages/features/IntegrationsPage';
import FraudPreventionPage from '../pages/features/FraudPreventionPage';
import VerifiedCredentialsPage from '../pages/features/VerifiedCredentialsPage';
import BiasFreeScoringPage from '../pages/features/BiasFreeScoringPage';
import PsychometricsPage from '../pages/PsychometricsPage';
import SelfAssessmentPage from '../pages/SelfAssessmentPage';
import LeadsInboxPage from '../pages/LeadsInboxPage';
import ConnectWithUsPage from '../pages/ConnectWithUsPage';
import AboutPage from '../pages/AboutPage';
import TeamPage, { TeamPageHold } from '../pages/TeamPage';
import { TEAM_PAGE_LIVE } from '../config/siteFlags';

const ROLES_ADMIN_RECRUITER = ['admin', 'recruiter'];
const ROLES_CANDIDATE = ['candidate'];
const ROLES_ADMIN = ['admin'];

function ProtectedRoute({ children, allowedRoles }) {
  const { user, signedOut } = useAuth();
  const location = useLocation();
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={signedOut ? '/login' : `/login?next=${next}`} replace />;
  }
  if (user.role === 'super_admin') return children;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'candidate') return <Navigate to="/portal" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role === 'candidate') return <Navigate to="/portal" replace />;
  return <Navigate to="/dashboard" replace />;
}

// Wrap an authed page in the app shell + role guard.
const guarded = (Page, roles) => (
  <ProtectedRoute allowedRoles={roles}>
    <AppLayout><Page /></AppLayout>
  </ProtectedRoute>
);

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/products/assessments" element={<AssessmentsProductPage />} />
      <Route path="/products/screening" element={<ScreeningProductPage />} />
      <Route path="/products/interviews" element={<InterviewsProductPage />} />
      <Route path="/products/engage" element={<EngageProductPage />} />
      <Route path="/products/compliance" element={<ComplianceProductPage />} />
      <Route path="/products/blockchain" element={<BlockchainProductPage />} />
      <Route path="/products/pipeline" element={<PipelineProductPage />} />
      <Route path="/features/certified-assessments" element={<CertifiedAssessmentsPage />} />
      <Route path="/features/anti-cheating" element={<AntiCheatingPage />} />
      <Route path="/features/nft-credentials" element={<NFTCredentialsPage />} />
      <Route path="/features/resume-intelligence" element={<ResumeIntelligencePage />} />
      <Route path="/features/ai-questions" element={<AIQuestionsPage />} />
      <Route path="/features/integrations" element={<IntegrationsFeaturePage />} />
      <Route path="/features/fraud-prevention" element={<FraudPreventionPage />} />
      <Route path="/features/verified-credentials" element={<VerifiedCredentialsPage />} />
      <Route path="/features/bias-free-scoring" element={<BiasFreeScoringPage />} />
      <Route path="/features/behavioral-assessments" element={<Navigate to="/psychometrics" replace />} />
      <Route path="/psychometrics" element={<PsychometricsPage />} />
      <Route path="/assessment" element={<SelfAssessmentPage />} />
      {/* Public: credential verification */}
      <Route path="/credentials/:tokenId" element={<CredentialPage />} />
      {/* Public: careers / job board distribution + lead capture */}
      <Route path="/careers" element={<PublicCareersPage />} />
      <Route path="/careers/:jobId" element={<PublicJobPage />} />
      {/* Public: company-scoped careers pages — skilliq.in/{company-slug}/careers */}
      <Route path="/:companySlug/careers" element={<PublicCareersPage />} />
      <Route path="/:companySlug/careers/:jobId" element={<PublicJobPage />} />
      <Route path="/auth/signin" element={<MagicLinkPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/case-study" element={<CaseStudyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/about/team" element={TEAM_PAGE_LIVE ? <TeamPage /> : <TeamPageHold />} />
      <Route path="/team" element={<Navigate to="/about/team" replace />} />
      <Route path="/connectwithus" element={<ConnectWithUsPage />} />
      <Route path="/connect" element={<Navigate to="/connectwithus" replace />} />
      <Route path="/legal/msa" element={<MSAPage />} />
      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/biometric-policy" element={<BiometricPolicyPage />} />
      <Route path="/legal/security" element={<SecurityPage />} />
      <Route path="/legal/data-request" element={<DataRequestPage />} />
      <Route path="/interview-data-request" element={<InterviewDataRequestPage />} />
      <Route path="/ai-transparency" element={<AITransparencyPage />} />
      <Route path="/payment" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_RECRUITER}><PaymentPage /></ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_RECRUITER}><PaymentReturnPage /></ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute allowedRoles={ROLES_ADMIN_RECRUITER}><PaymentReturnPage /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={guarded(CommandCenterPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/command-center" element={<Navigate to="/dashboard" replace />} />
      <Route path="/jobs" element={guarded(JobsPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/jd-builder" element={guarded(JDBuilderPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/pipeline" element={guarded(PipelinePage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/analytics" element={guarded(AnalyticsPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/credit-usage" element={guarded(CreditUsagePage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/settings" element={guarded(SettingsPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/billing" element={guarded(BillingPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/jobs/:jobId" element={guarded(JobDetailPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/candidates/:candidateId" element={guarded(CandidateDetailPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/inbox" element={guarded(LeadsInboxPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/assessments" element={guarded(AssessmentsGlobalPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/jobs/:jobId/assessments" element={guarded(AssessmentListPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/assessments/:assessmentId/results" element={guarded(AssessmentResultsPage, ROLES_ADMIN_RECRUITER)} />
      {/* Public route - candidates take assessment without auth */}
      <Route path="/take-assessment/:accessCode" element={<TakeAssessmentPage />} />
      {/* Behavioral / Personality assessment (DISC-style) */}
      <Route path="/behavioral-assessments" element={guarded(BehavioralAssessmentsPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/behavioral-assessments/:sessionId" element={guarded(BehavioralReportPage, ROLES_ADMIN_RECRUITER)} />
      {/* Public route - candidates take psychometric assessment via invite token */}
      <Route path="/behavioral/:token" element={<BehavioralTakePage />} />
      {/* Interview routes */}
      <Route path="/interviews" element={guarded(AIInterviewsHubPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/ai-interviews" element={<Navigate to="/interviews" replace />} />
      <Route path="/ai-interviews/plans/:planId" element={guarded(AIInterviewPlanPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/ai-interviews/:id" element={guarded(AIInterviewReportPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/jobs/:jobId/interviews" element={guarded(InterviewListPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/interviews/:interviewId/results" element={guarded(InterviewResultsPage, ROLES_ADMIN_RECRUITER)} />
      {/* Public route - candidates join interview */}
      <Route path="/join-interview/:accessCode" element={<InterviewRoomPage />} />
      <Route path="/ai-interview/:token" element={<AIInterviewCandidatePage />} />
      <Route path="/scheduling" element={guarded(SchedulingPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/schedule/:token" element={<CandidateSchedulePage />} />
      <Route path="/join/:token" element={<CandidateJoinPage />} />
      <Route path="/interview-room/:id" element={<ProtectedRoute allowedRoles={ROLES_ADMIN_RECRUITER}><HumanInterviewRoomPage /></ProtectedRoute>} />
      <Route path="/shared/ai-report/:token" element={<SharedAIReportPage />} />
      {/* Candidate Portal */}
      <Route path="/portal" element={guarded(CandidatePortalPage, ROLES_CANDIDATE)} />
      <Route path="/portal/application/:jobId" element={guarded(ApplicationDetailPage, ROLES_CANDIDATE)} />
      {/* Admin Dashboard */}
      <Route path="/admin" element={guarded(AdminDashboardPage, ROLES_ADMIN)} />
      <Route path="/admin/consent-logs" element={guarded(ConsentLogPage, ROLES_ADMIN)} />
      <Route path="/admin/data-requests" element={guarded(DataRequestsAdminPage, ROLES_ADMIN)} />
      <Route path="/admin/candidate-requests" element={guarded(CandidateRequestsAdminPage, ROLES_ADMIN)} />
      {/* Verification */}
      <Route path="/verification" element={guarded(VerificationDashboardPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/applications" element={guarded(ApplicationsPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/resume-intelligence" element={guarded(ResumeIntelligenceListPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/sourcing" element={guarded(SourcingPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/careers-page" element={guarded(CareersManagementPage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/credentials" element={guarded(CredentialsManagePage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/compliance" element={guarded(CompliancePage, ROLES_ADMIN_RECRUITER)} />
      <Route path="/verification/:requestId" element={guarded(VerificationDetailPage, ROLES_ADMIN_RECRUITER)} />
      {/* Public: candidate document upload */}
      <Route path="/verify/:accessCode" element={<CandidateVerificationPage />} />
      <Route path="*" element={<RoleRedirect />} />
    </Routes>
  );
}
